// Visuals and timing ported from purchase_effect_candidates.html (A → F).
export const PURCHASE_EFFECTS = ['A', 'B', 'C', 'D', 'E', 'F'] as const;
export const PURCHASE_SUCCESS_MS = 1900;
// The longest reference particle lasts 1800 + 520ms. Finish it before the next run.
export const PURCHASE_EFFECT_MS = 2400;

const rand = (min: number, max: number) => min + Math.random() * (max - min);
const pick = <T,>(values: readonly T[]): T => values[Math.floor(Math.random() * values.length)];
type Origin = { x: number; y: number; w: number; h: number; cx: number; cy: number };
const star = (color: string) => `<svg viewBox="0 0 24 24" width="100%" height="100%"><path d="M12 0C12.8 7.2 16.8 11.2 24 12C16.8 12.8 12.8 16.8 12 24C11.2 16.8 7.2 12.8 0 12C7.2 11.2 11.2 7.2 12 0Z" fill="${color}"/></svg>`;
const heart = (color: string) => `<svg viewBox="0 0 24 24" width="100%" height="100%"><path d="M12 21s-7.5-4.6-9.6-9.2C.9 8.4 2.6 4.5 6.3 4.5c2.1 0 3.9 1.2 5.7 3.3 1.8-2.1 3.6-3.3 5.7-3.3 3.7 0 5.4 3.9 3.9 7.3C19.5 16.4 12 21 12 21z" fill="${color}"/></svg>`;
const bag = '<svg viewBox="0 0 24 24" width="14" height="14" fill="#fff"><path d="M6 8h12l1 12H5L6 8z"/><path d="M9 8V7a3 3 0 016 0v1" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round"/></svg>';

export function playPurchaseEffect(index: number, layer: HTMLElement, button: HTMLElement, card: HTMLElement) {
  const animations = new Set<Animation>();
  const nodes = new Set<HTMLElement>();
  const timers = new Set<number>();
  const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const cleanup = () => {
    timers.forEach(timer => window.clearTimeout(timer));
    animations.forEach(animation => animation.cancel());
    nodes.forEach(node => node.remove());
    timers.clear(); animations.clear(); nodes.clear();
    media?.removeEventListener?.('change', onMotionChange);
  };
  const onMotionChange = () => { if (media?.matches) cleanup(); };
  if (media?.matches || typeof button.animate !== 'function') return cleanup;
  media?.addEventListener?.('change', onMotionChange);

  // Measure before animating the button; transforms must not move the origin.
  const frame = layer.getBoundingClientRect();
  const rel = (element: HTMLElement): Origin => {
    const rect = element.getBoundingClientRect();
    const x = rect.left - frame.left, y = rect.top - frame.top;
    return { x, y, w: rect.width, h: rect.height, cx: x + rect.width / 2, cy: y + rect.height / 2 };
  };
  const origin = rel(button), bounds = rel(card);
  const radius = getComputedStyle(card).borderRadius || '12px';
  const cardBox = `left:${bounds.x}px;top:${bounds.y}px;width:${bounds.w}px;height:${bounds.h}px;border-radius:${radius};`;
  const make = (css: string, svg = '') => {
    const node = document.createElement('div');
    node.style.cssText = `position:absolute;box-sizing:border-box;will-change:transform,opacity;${css}`;
    // Only the fixed SVG paths and palette constants above are used as markup.
    if (svg) node.innerHTML = svg;
    layer.appendChild(node);
    nodes.add(node);
    return node;
  };
  const animate = (node: HTMLElement, frames: Keyframe[], options: KeyframeAnimationOptions, remove = true) => {
    const animation = node.animate(frames, { ...options, fill: 'both' });
    animations.add(animation);
    animation.onfinish = () => {
      if (remove) {
        node.remove(); nodes.delete(node);
        animation.cancel(); animations.delete(animation);
      }
    };
    return animation;
  };
  const later = (callback: () => void, delay: number) => {
    const timer = window.setTimeout(() => { timers.delete(timer); callback(); }, delay);
    timers.add(timer);
  };
  const sparkle = (x: number, y: number, color: string, size: number, duration: number, delay: number, turn = 90) => {
    const node = make(`left:${x}px;top:${y}px;width:${size}px;height:${size}px;`, star(color));
    animate(node, [
      { transform: 'translate(-50%,-50%) scale(0) rotate(0deg)', opacity: 0 },
      { transform: `translate(-50%,-50%) scale(1) rotate(${turn / 2}deg)`, opacity: 1, offset: .45 },
      { transform: `translate(-50%,-50%) scale(0) rotate(${turn}deg)`, opacity: 0 },
    ], { duration, delay });
  };

  function gold(o: Origin) {
    const palette = pick([
      ['#fde68a', '#fbbf24', '#ffffff', '#fcd34d'],
      ['#fecdd3', '#fda4af', '#ffffff', '#fbcfe8'],
      ['#ddd6fe', '#c4b5fd', '#ffffff', '#e9d5ff'],
    ]);
    const count = Math.round(rand(26, 36));
    for (let i = 0; i < count; i++) {
      const color = pick(palette), size = rand(2.6, 5), x = o.x + rand(10, o.w - 10);
      const node = make(`left:${x}px;top:${o.cy + rand(-14, 10)}px;width:${size}px;height:${size}px;border-radius:50%;background:${color};box-shadow:0 0 ${size * 2.4}px ${color};`);
      const up = rand(60, 150), sway = rand(-18, 18);
      animate(node, [
        { transform: 'translate(0,0) scale(.3)', opacity: 0 },
        { transform: `translate(${sway * .5}px,${-up * .45}px) scale(1)`, opacity: 1, offset: .25 },
        { transform: `translate(${sway}px,${-up * .75}px) scale(.8)`, opacity: .35, offset: .55 },
        { transform: `translate(${sway * .7}px,${-up}px) scale(.9)`, opacity: .9, offset: .75 },
        { transform: `translate(${sway * .4}px,${-up - 14}px) scale(.2)`, opacity: 0 },
      ], { duration: rand(1100, 1700), delay: rand(0, 350), easing: 'cubic-bezier(.25,.6,.35,1)' });
    }
  }

  function beam() {
    const colors = pick([['#f43f5e', '#ec4899'], ['#a855f7', '#6366f1'], ['#10b981', '#2dd4bf'], ['#fbbf24', '#f97316']]);
    const turns = pick([1, 1, 1.5]), direction = pick([1, -1]), duration = rand(900, 1150);
    const mask = 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)';
    const wrap = make(`${cardBox}padding:2px;overflow:hidden;-webkit-mask:${mask};-webkit-mask-composite:xor;mask:${mask};mask-composite:exclude;`);
    const diameter = Math.hypot(bounds.w, bounds.h) * 1.1, start = rand(-25, 25);
    const spin = document.createElement('div');
    spin.style.cssText = `position:absolute;left:50%;top:50%;width:${diameter}px;height:${diameter}px;margin:${-diameter / 2}px 0 0 ${-diameter / 2}px;background:conic-gradient(from 0deg,transparent 0 62%,${colors[0]} 82%,#fff 96%,transparent 100%);`;
    wrap.appendChild(spin);
    animate(spin, [{ transform: `rotate(${start}deg)` }, { transform: `rotate(${start + direction * 360 * turns}deg)` }], { duration, easing: 'cubic-bezier(.45,.05,.3,1)' }, false);
    animate(wrap, [{ opacity: 1 }, { opacity: 1, offset: .7 }, { opacity: 0 }], { duration: duration + 120 });
    const glow = make(`${cardBox}box-shadow:0 0 22px ${colors[1]}88,inset 0 0 14px ${colors[1]}33;`);
    animate(glow, [{ opacity: 0 }, { opacity: 1, offset: .35 }, { opacity: 0 }], { duration: duration + 200, easing: 'ease-out' });
  }

  function firework(o: Origin) {
    const palette = pick([['#fb7185', '#f472b6', '#ffffff'], ['#fbbf24', '#fde68a', '#ffffff'], ['#c084fc', '#a78bfa', '#ffffff'], ['#34d399', '#5eead4', '#ffffff']]);
    const shots = pick([1, 1, 2]);
    for (let shot = 0; shot < shots; shot++) {
      const cx = o.cx + rand(-70, 70), cy = o.cy - rand(120, 165) + (shot ? rand(-10, 20) : 0), delay = shot * 260;
      const trail = make(`left:${o.cx}px;top:${o.cy}px;width:2px;height:22px;border-radius:2px;background:linear-gradient(to top,transparent,#fff);`);
      animate(trail, [
        { transform: 'translate(-50%,0) translate(0,0)', opacity: 0 },
        { transform: `translate(-50%,0) translate(${(cx - o.cx) * .6}px,${(cy - o.cy) * .6}px)`, opacity: 1, offset: .5 },
        { transform: `translate(-50%,0) translate(${cx - o.cx}px,${cy - o.cy}px)`, opacity: 0 },
      ], { duration: 300, delay, easing: 'cubic-bezier(.3,.6,.4,1)' });
      later(() => {
        const flash = make(`left:${cx}px;top:${cy}px;width:26px;height:26px;border-radius:50%;background:radial-gradient(circle,#fff,${palette[0]}00 70%);`);
        animate(flash, [{ transform: 'translate(-50%,-50%) scale(.3)', opacity: 1 }, { transform: 'translate(-50%,-50%) scale(1.6)', opacity: 0 }], { duration: 420, easing: 'ease-out' });
        const count = Math.round(rand(12, 16)), reach = rand(34, 52);
        for (let i = 0; i < count; i++) {
          const angle = 360 / count * i + rand(-5, 5), length = rand(9, 15), color = palette[i % palette.length];
          const line = make(`left:${cx}px;top:${cy}px;width:2px;height:${length}px;border-radius:2px;background:${color};transform-origin:50% 0;`);
          animate(line, [
            { transform: `rotate(${angle}deg) translateY(4px) scaleY(.2)`, opacity: 1 },
            { transform: `rotate(${angle}deg) translateY(${reach * .8}px) scaleY(1)`, opacity: 1, offset: .45 },
            { transform: `rotate(${angle}deg) translateY(${reach + 8}px) scaleY(.3)`, opacity: 0 },
          ], { duration: rand(650, 820), easing: 'cubic-bezier(.1,.8,.3,1)' });
          const dot = make(`left:${cx}px;top:${cy}px;width:3px;height:3px;border-radius:50%;background:${color};`);
          const radians = angle * Math.PI / 180, tx = Math.sin(radians) * (reach + 16), ty = -Math.cos(radians) * (reach + 16);
          animate(dot, [
            { transform: 'translate(-50%,-50%) translate(0,0)', opacity: 0 },
            { transform: `translate(-50%,-50%) translate(${tx * .8}px,${ty * .8}px)`, opacity: 1, offset: .5 },
            { transform: `translate(-50%,-50%) translate(${tx}px,${ty + 10}px)`, opacity: 0 },
          ], { duration: rand(800, 1000), delay: 80, easing: 'ease-out' });
        }
      }, 300 + delay);
    }
  }

  function hearts(o: Origin) {
    const palette = ['#fb7185', '#f472b6', '#c084fc', '#ffffff', '#fda4af'];
    const count = Math.round(rand(6, 9)), fromRight = Math.random() < .65;
    for (let i = 0; i < count; i++) {
      const size = rand(14, 24), color = pick(palette);
      const x = o.x + o.w * (fromRight ? rand(.62, .92) : rand(.15, .85));
      const node = make(`left:${x}px;top:${o.cy - 6}px;width:${size}px;height:${size}px;filter:drop-shadow(0 0 6px ${color}88);`, heart(color));
      const up = rand(140, 230), sway = rand(10, 22), rotate = rand(10, 28);
      animate(node, [
        { transform: 'translate(-50%,-50%) translate(0,0) scale(.3) rotate(0deg)', opacity: 0 },
        { transform: `translate(-50%,-50%) translate(${sway}px,${-up * .3}px) scale(1) rotate(${rotate}deg)`, opacity: 1, offset: .2 },
        { transform: `translate(-50%,-50%) translate(${-sway}px,${-up * .6}px) scale(1) rotate(${-rotate}deg)`, opacity: .95, offset: .55 },
        { transform: `translate(-50%,-50%) translate(${sway * .6}px,${-up}px) scale(.85) rotate(${rotate * .6}deg)`, opacity: 0 },
      ], { duration: rand(1300, 1800), delay: rand(0, 520), easing: 'cubic-bezier(.3,.5,.4,1)' });
    }
  }

  function chip(o: Origin) {
    const text = pick(['득템 +1', '잘 담았어요', '오늘의 득템']);
    const gradient = pick(['linear-gradient(90deg,#f43f5e,#ec4899)', 'linear-gradient(90deg,#8b5cf6,#ec4899)', 'linear-gradient(90deg,#10b981,#2dd4bf)']);
    const dx = rand(-50, 50);
    const node = make(`left:${o.cx + dx}px;top:${o.cy - 30}px;display:flex;align-items:center;gap:6px;padding:6px 12px;border-radius:999px;font-size:12px;font-weight:700;color:#fff;white-space:nowrap;background:${gradient};box-shadow:0 6px 20px rgba(0,0,0,.35),0 0 18px rgba(236,72,153,.35);`, bag);
    const label = document.createElement('span');
    label.textContent = text;
    node.appendChild(label);
    animate(node, [
      { transform: 'translate(-50%,-50%) translateY(14px) scale(.6)', opacity: 0, easing: 'cubic-bezier(.16,1,.3,1)' },
      { transform: 'translate(-50%,-50%) translateY(-6px) scale(1.04)', opacity: 1, offset: .25 },
      { transform: 'translate(-50%,-50%) translateY(-22px) scale(1)', opacity: 1, offset: .7 },
      { transform: 'translate(-50%,-50%) translateY(-44px) scale(.96)', opacity: 0 },
    ], { duration: rand(1250, 1500) });
    const count = Math.round(rand(2, 4));
    for (let i = 0; i < count; i++) {
      sparkle(o.cx + dx + rand(-70, 70), o.cy - 30 + rand(-26, 16), i % 2 ? '#fff' : '#fcd34d', rand(8, 13), rand(600, 850), rand(120, 520));
    }
  }

  function aurora() {
    const colors = pick([['#f43f5e', '#a855f7', '#2dd4bf'], ['#fbbf24', '#f43f5e', '#8b5cf6'], ['#34d399', '#60a5fa', '#c084fc']]);
    const halo = make(`${cardBox}box-shadow:-14px 2px 30px ${colors[0]},14px 2px 30px ${colors[2]},0 -12px 30px ${colors[1]},0 10px 26px ${colors[1]}88;`);
    animate(halo, [{ opacity: 0, transform: 'scale(.97)' }, { opacity: .85, transform: 'scale(1.01)', offset: .35 }, { opacity: 0, transform: 'scale(1.03)' }], { duration: rand(1000, 1300), easing: 'ease-out' });
    const ring = make(`${cardBox}border:1.5px solid rgba(255,255,255,.8);`);
    animate(ring, [{ opacity: .9, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(1.045,1.18)' }], { duration: rand(700, 820), easing: 'cubic-bezier(.16,1,.3,1)' });
    const count = Math.round(rand(5, 7));
    for (let i = 0; i < count; i++) {
      sparkle(bounds.x + rand(10, bounds.w - 10), bounds.y + rand(-6, bounds.h * .8), i % 2 ? '#ffffff' : colors[i % 3], rand(8, 13), rand(550, 800), rand(0, 500), 60);
    }
  }

  animate(button, [
    { transform: 'scale(1)' }, { transform: 'scale(.965)', offset: .18 },
    { transform: `scale(${rand(1.02, 1.05)})`, offset: .55 }, { transform: 'scale(1)' },
  ], { duration: rand(380, 480), easing: 'cubic-bezier(.16,1,.3,1)' }, false);
  [gold, beam, firework, hearts, chip, aurora][index % PURCHASE_EFFECTS.length](origin);
  return cleanup;
}
